import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../App";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { styledToast as toast } from "@/components/ui/sonner";
import { useTranslation } from 'react-i18next';

export default function NotificationBell() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadUnreadCount, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to load notifications");
    }
  };

  const loadUnreadCount = async () => {
    try {
      const countRes = await api.get("/notifications/unread-count");
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to load unread count");
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      await api.put(`/notifications/${notification.id}/read`);
      if (notification.application_id) {
        navigate(`/application/${notification.application_id}`);
      }
      setOpen(false);
      loadNotifications();
    } catch (error) {
      toast.error("Failed to mark notification as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/mark-all-read");
      loadNotifications();
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
          onClick={() => {
            setOpen(true);
            if (open === false) loadNotifications();
          }}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notification-dropdown">
        <div className="p-4 border-b border-stone-200 flex justify-between items-center">
          <h3 className="font-manrope font-bold text-slate-900">{t('notifications.title')}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              className="text-xs text-emerald-800 hover:text-emerald-900"
              data-testid="mark-all-read"
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <ScrollArea className="h-96">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">{t('notifications.noNotifications')}</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-200">
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`p-4 cursor-pointer hover:bg-stone-50 transition-colors ${
                    !notif.is_read ? "bg-emerald-50/50" : ""
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                    {!notif.is_read && (
                      <span className="w-2 h-2 bg-emerald-600 rounded-full flex-shrink-0 mt-1.5"></span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mb-2">{notif.message}</p>
                  <p className="text-xs text-slate-400">
                    {format(new Date(notif.created_at), "MMM dd, yyyy 'at' h:mm a")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}